import React, { useState, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import {
  Button, Dialog, Portal, TextInput, Chip, Text, List,
} from 'react-native-paper';
import { AGE_CATEGORY, GRADE } from '../../utils/constant';

const buildRulesFromState = (selectedCategories, selectedGrades, minAge, maxAge) => {
  const rules = {
    profile: {},
  };

  if (selectedGrades.length > 0) {
    rules.profile.grade = { in: selectedGrades };
  }

  const ageConditions = {};
  if (maxAge) {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - parseInt(maxAge, 10));
    ageConditions.gte = maxDate.toISOString();
  }
  if (minAge) {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - parseInt(minAge, 10));
    ageConditions.lte = minDate.toISOString();
  }

  if (selectedCategories.length > 0) {
    const categoryConditions = selectedCategories.map((catName) => {
      const category = Object.values(AGE_CATEGORY).find((c) => c.name === catName);
      if (!category) return null;
      const catMinDate = new Date();
      catMinDate.setFullYear(catMinDate.getFullYear() - (category.maxYear + 1));
      const catMaxDate = new Date();
      catMaxDate.setFullYear(catMaxDate.getFullYear() - category.minYear);
      return {
        dateOfBirth: {
          gte: catMinDate.toISOString(),
          lte: catMaxDate.toISOString(),
        },
      };
    }).filter(Boolean);

    if (categoryConditions.length > 0) {
      rules.profile.OR = categoryConditions.map((c) => ({ ...c }));
    }
  }

  if (Object.keys(ageConditions).length > 0) {
    rules.profile.dateOfBirth = { ...rules.profile.dateOfBirth, ...ageConditions };
  }

  if (Object.keys(rules.profile).length === 0) {
    return '';
  }

  return JSON.stringify(rules, null, 2);
};

const parseRulesToState = (rules, setCategories, setGrades, setMinAge, setMaxAge) => {
  try {
    if (!rules) {
      setCategories([]);
      setGrades([]);
      setMinAge('');
      setMaxAge('');
      return;
    }
    const parsedRules = JSON.parse(rules);
    const profileRules = parsedRules.profile || {};

    // Parse grades
    if (profileRules.grade && profileRules.grade.in) {
      setGrades(profileRules.grade.in);
    }

    // Parse age
    if (profileRules.dateOfBirth) {
      if (profileRules.dateOfBirth.gte) {
        const maxDate = new Date(profileRules.dateOfBirth.gte);
        const age = new Date().getFullYear() - maxDate.getFullYear();
        setMaxAge(String(age));
      }
      if (profileRules.dateOfBirth.lte) {
        const minDate = new Date(profileRules.dateOfBirth.lte);
        const age = new Date().getFullYear() - minDate.getFullYear();
        setMinAge(String(age));
      }
    }

    // Parse categories from OR conditions
    if (profileRules.OR) {
      const categories = profileRules.OR.map((condition) => {
        if (condition.dateOfBirth && condition.dateOfBirth.gte && condition.dateOfBirth.lte) {
          const gteDate = new Date(condition.dateOfBirth.gte);
          const lteDate = new Date(condition.dateOfBirth.lte);
          const maxYear = (new Date().getFullYear() - gteDate.getFullYear()) - 1;
          const minYear = new Date().getFullYear() - lteDate.getFullYear();

          const category = Object.values(AGE_CATEGORY).find(
            (c) => c.minYear === minYear && c.maxYear === maxYear,
          );
          return category ? category.name : null;
        }
        return null;
      }).filter(Boolean);
      setCategories(categories);
    }
  } catch (e) {
    // Not a valid JSON or structure, ignore and leave fields empty
  }
};

function AccessPolicyDialogs({
  dialogVisible,
  hideDialog,
  currentPolicy,
  name,
  setName,
  description,
  setDescription,
  rules,
  setRules,
  handleSave,
}) {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');

  // Effect to initialize UI state from `rules` prop when dialog opens
  useEffect(() => {
    if (dialogVisible) {
      parseRulesToState(rules, setSelectedCategories, setSelectedGrades, setMinAge, setMaxAge);
    }
  }, [dialogVisible]);

  // Effect to update `rules` prop when UI state changes
  useEffect(() => {
    // Do not run on initial render before the dialog is visible
    if (dialogVisible) {
      const newRules = buildRulesFromState(selectedCategories, selectedGrades, minAge, maxAge);
      if (newRules !== rules) {
        setRules(newRules);
      }
    }
  }, [selectedCategories, selectedGrades, minAge, maxAge, dialogVisible]);

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => (prev.includes(category)
      ? prev.filter((c) => c !== category)
      : [...prev, category]));
  };

  const toggleGrade = (grade) => {
    setSelectedGrades((prev) => (prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]));
  };

  return (
    <Portal>
      <Dialog visible={dialogVisible} onDismiss={hideDialog}>
        <Dialog.Title>{currentPolicy ? 'Modifier la politique' : 'Nouvelle politique'}</Dialog.Title>
        <Dialog.Content>
          <ScrollView>
            <TextInput
              label="Nom"
              value={name}
              onChangeText={setName}
              style={{ marginBottom: 10 }}
            />
            <TextInput
              label="Description"
              value={description}
              onChangeText={setDescription}
              style={{ marginBottom: 10 }}
            />

            <Text variant="titleMedium" style={{ marginTop: 20, marginBottom: 10 }}>Constructeur de règles</Text>

            <Text>Catégories</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 10 }}>
              {Object.values(AGE_CATEGORY).map((cat) => (
                <Chip
                  key={cat.name}
                  selected={selectedCategories.includes(cat.name)}
                  onPress={() => toggleCategory(cat.name)}
                  style={{ margin: 2 }}
                >
                  {cat.name}
                </Chip>
              ))}
            </View>

            <Text>Grades</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 10 }}>
              {Object.entries(GRADE).map(([key, value]) => (
                <Chip
                  key={key}
                  selected={selectedGrades.includes(key)}
                  onPress={() => toggleGrade(key)}
                  style={{ margin: 2 }}
                >
                  {value}
                </Chip>
              ))}
            </View>

            <Text>Âge</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 }}>
              <TextInput
                label="Âge min"
                value={minAge}
                onChangeText={setMinAge}
                keyboardType="numeric"
                style={{ width: '48%' }}
              />
              <TextInput
                label="Âge max"
                value={maxAge}
                onChangeText={setMaxAge}
                keyboardType="numeric"
                style={{ width: '48%' }}
              />
            </View>
            <List.Section>
              <List.Accordion
                title="Afficher le JSON brut (avancé)"
                left={(props) => <List.Icon {...props} icon="code-json" />}
              >
                <TextInput
                  label="Règles (JSON brut)"
                  value={rules}
                  onChangeText={setRules}
                  multiline
                  numberOfLines={10}
                  style={{ marginBottom: 10, marginTop: 10 }}
                />
              </List.Accordion>
            </List.Section>
          </ScrollView>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={hideDialog}>Annuler</Button>
          <Button onPress={handleSave}>Enregistrer</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}

export default AccessPolicyDialogs;
